/**
 * pm-gallery.js — Product media gallery (Tier 1 interactions)
 * IIFE pattern per Slice 4 LOCKED #18. Direct query-and-bind, no custom element.
 * Dependencies: none. Loaded always alongside _product-media.
 *
 * Phase 2.4 scope: thumb/arrow/dot/keyboard nav, scroll-snap sync via
 * IntersectionObserver, product:variant-changed LISTENER only.
 * Dispatcher for variant sync lands with the variant picker (Phase 2.5+),
 * where dual-fire of both product:variant-changed and variant:changed
 * keeps section-sticky-atc in sync during the transition.
 */

(function () {
  'use strict';

  var GUARD = '__pmGalleryInit';

  /* S41-M1-B10 — scroll behaviour for every programmatic scroll this file makes, honouring
     the OS reduced-motion preference. Precedent: component-carousel.js:128-129.
     B-6b extended it to setActive's mainEl.scrollTo, which had carried a literal 'smooth'
     since Phase 2.4: the main rail, the strip arrows and the strip auto-scroll now all obey
     the same preference, and no scroll in this file animates against it. */
  var PM_SCROLL = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';

  function init(root) {
    if (!root || root[GUARD]) return;

    var mainEl = root.querySelector('[data-pm-main]');
    if (!mainEl) return;

    var slides = [].slice.call(mainEl.querySelectorAll('.pm-gallery__slide'));
    if (slides.length === 0) return;

    var thumbs = [].slice.call(root.querySelectorAll('[data-pm-thumbs] button'));
    var dots = [].slice.call(root.querySelectorAll('[data-pm-dot]'));
    var prevBtn = root.querySelector('[data-pm-nav="prev"]');
    var nextBtn = root.querySelector('[data-pm-nav="next"]');
    var counterEl = root.querySelector('[data-pm-counter-current]');
    var total = slides.length;

    root._pmIndex = 0;

    function setActive(index, options) {
      options = options || {};
      if (index < 0 || index >= total) return;
      var prev = root._pmIndex;
      if (prev === index && !options.force) return;

      if (slides[prev]) slides[prev].classList.remove('pm-gallery__slide--active');
      slides[index].classList.add('pm-gallery__slide--active');

      thumbs.forEach(function (t, i) {
        var active = i === index;
        t.classList.toggle('pm-gallery__thumb--active', active);
        t.setAttribute('aria-current', active ? 'true' : 'false');
      });

      dots.forEach(function (d, i) {
        d.classList.toggle('pm-gallery__dot--active', i === index);
      });

      if (counterEl && !options.skipCounter) {
        counterEl.textContent = String(index + 1);
      }

      if (!options.skipScroll && mainEl.scrollWidth > mainEl.clientWidth + 1) {
        mainEl.scrollTo({ left: slides[index].offsetLeft, behavior: PM_SCROLL });
      }

      root._pmIndex = index;

      /* S41-M1-B10 / U3 — THE choke point, and the only call site of scrollThumbIntoView.
         All six sources that can change the active image funnel through setActive (thumb
         click, gallery arrows, keyboard, the rail's IntersectionObserver, the rail scroll
         listener below, the variant swap), so one call covers them all.
         NOT gated on options.skipScroll: that flag stops the rail feeding its own scroll
         back into mainEl above, and reusing it here would kill exactly the case U3 most
         needs — swipe the mobile carousel, the strip follows. At init it is a no-op by
         arithmetic, not by a guard: thumb 0 at offset 0 gives a zero delta on both axes. */
      scrollThumbIntoView(root, thumbs[index]);
    }

    thumbs.forEach(function (thumb, i) {
      thumb.addEventListener('click', function () { setActive(i); });
    });

    dots.forEach(function (dot) {
      dot.addEventListener('click', function () {
        var i = parseInt(dot.getAttribute('data-pm-dot'), 10);
        if (!isNaN(i)) setActive(i);
      });
    });

    if (prevBtn) {
      prevBtn.addEventListener('click', function () {
        setActive((root._pmIndex - 1 + total) % total);
      });
    }
    if (nextBtn) {
      nextBtn.addEventListener('click', function () {
        setActive((root._pmIndex + 1) % total);
      });
    }

    function onKeydown(e) {
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        setActive((root._pmIndex - 1 + total) % total);
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        setActive((root._pmIndex + 1) % total);
      }
    }
    root.addEventListener('keydown', onKeydown);
    root._pmKeyHandler = onKeydown;

    if ('IntersectionObserver' in window && mainEl.scrollWidth > mainEl.clientWidth + 1) {
      var observer = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
            var idx = slides.indexOf(entry.target);
            if (idx !== -1 && idx !== root._pmIndex) {
              setActive(idx, { skipScroll: true });
            }
          }
        });
      }, { root: mainEl, threshold: 0.5 });

      slides.forEach(function (s) { observer.observe(s); });
      root._pmObserver = observer;
    }

    /* S41-M1-B10 / U3 — the rail's scroll position, routed into setActive so the highlight
       and the strip auto-scroll follow a swipe. Bound UNCONDITIONALLY, which is the point:
       the IntersectionObserver above is built once behind a width-dependent overflow test,
       so a page LOADED at desktop under thumbnails_left (slides absolutely stacked,
       scrollWidth == clientWidth) gets no observer, and narrowing to mobile left the
       carousel unsynced — the hole the B7 plan logged. Costs nothing where the rail cannot
       scroll: a non-scrolling element fires no scroll events. The overlap with the observer
       is idempotent (setActive early-returns on an unchanged index; the test below skips
       even that). Nearest slide by offsetLeft, the same coordinate setActive scrolls TO, so
       read and write cannot disagree about what "slide i is showing" means.
       rAF-throttled and passive, the shape component-bundle-products.js:534-548 uses. */
    var railTicking = false;
    function onRailScroll() {
      if (railTicking) return;
      railTicking = true;
      window.requestAnimationFrame(function () {
        railTicking = false;
        if (mainEl.scrollWidth <= mainEl.clientWidth + 1) return;
        var pos = mainEl.scrollLeft;
        var nearest = 0;
        var best = Infinity;
        for (var i = 0; i < slides.length; i++) {
          var d = Math.abs(slides[i].offsetLeft - pos);
          if (d < best) { best = d; nearest = i; }
        }
        if (nearest !== root._pmIndex) setActive(nearest, { skipScroll: true });
      });
    }
    mainEl.addEventListener('scroll', onRailScroll, { passive: true });
    root._pmRailScrollTeardown = function () {
      mainEl.removeEventListener('scroll', onRailScroll);
      root._pmRailScrollTeardown = null;
    };

    function onVariantChange(e) {
      var variant = e.detail && e.detail.variant;
      if (!variant || !variant.id) return;

      if (variant.featured_media && variant.featured_media.id) {
        var mediaId = String(variant.featured_media.id);
        for (var i = 0; i < slides.length; i++) {
          if (slides[i].getAttribute('data-media-id') === mediaId) {
            setActive(i);
            if (root._pmThumbArrowsSync) root._pmThumbArrowsSync();
            return;
          }
        }
      }

      var target = String(variant.id);
      for (var j = 0; j < slides.length; j++) {
        var ids = slides[j].getAttribute('data-variant-ids');
        if (!ids) continue;
        if (ids.split(/\s+/).indexOf(target) !== -1) {
          setActive(j);
          if (root._pmThumbArrowsSync) root._pmThumbArrowsSync();
          return;
        }
      }
    }
    document.addEventListener('product:variant-changed', onVariantChange);
    root._pmVariantHandler = onVariantChange;

    setupThumbArrows(root);

    root.setAttribute('data-pm-js', '');
    setActive(0, { force: true, skipScroll: true });

    var zoomBehavior = root.getAttribute('data-zoom-behavior');
    if (zoomBehavior && zoomBehavior !== 'none') {
      setupZoomLoader(root, zoomBehavior);
    }

    root[GUARD] = true;
  }

  /**
   * S41-M1-B7 / B10 — thumbnail-strip arrows. One pair per AXIS, bound per shell.
   * The markup renders up to two pairs against the same strip, because the shipped default
   * pairs a VERTICAL desktop strip (thumbnails_left) with a HORIZONTAL mobile one
   * (carousel_with_thumbs) on the same element. Each pair is driven independently and is
   * shown only while the strip really overflows ON ITS OWN AXIS.
   * That axis test is what retired B7's matchMedia('(min-width: 1025px)') gate: the strip's
   * axis IS its width state, the test re-measures on every sync, and it needs no listener
   * at the 1024/1025 boundary and no second lock in the CSS. The wrong-axis pair reports
   * zero overflow and stays hidden, so both can ship on one element safely.
   * The buttons are server-rendered inert (`hidden`); this decides whether they appear at
   * all. No-op when the markup rendered no pair — Arrows: off, or a layout with no strip:
   * the per-axis lookup finds nothing and nothing is bound.
   * Arithmetic and listener shapes are component-bundle-products.js's (syncEnds :83-91,
   * overflow test :101, rAF scroll listener :534-548, debounced resize :550-561).
   */
  function setupThumbArrows(root) {
    var strip = root.querySelector('[data-pm-thumbs]');
    if (!strip) return;
    var shell = strip.closest('.pm-gallery__thumbs-shell') || root;

    var pairs = [];
    ['x', 'y'].forEach(function (axis) {
      var prev = shell.querySelector('[data-pm-thumbs-prev][data-pm-thumbs-axis="' + axis + '"]');
      var next = shell.querySelector('[data-pm-thumbs-next][data-pm-thumbs-axis="' + axis + '"]');
      if (prev && next) pairs.push(bindThumbArrowPair(strip, prev, next, axis));
    });
    if (pairs.length === 0) return;

    function syncAll() {
      for (var i = 0; i < pairs.length; i++) pairs[i].sync();
    }

    root._pmThumbArrowsSync = syncAll;
    root._pmThumbArrowsTeardown = function () {
      for (var i = 0; i < pairs.length; i++) pairs[i].teardown();
      pairs = [];
      root._pmThumbArrowsSync = null;
      root._pmThumbArrowsTeardown = null;
    };

    /* First sync waits for `load`, not DOMContentLoaded: the arrows' geometry lives in
       pm-gallery.css, a media="print" link whose onload swap can still be in flight when
       this defer script runs. Stylesheets delay `load`, so by then the geometry is applied
       and unhiding cannot flash unstyled in-flow buttons into the gallery grid. */
    if (document.readyState === 'complete') syncAll();
    else window.addEventListener('load', syncAll, { once: true });
  }

  /**
   * One arrow pair, one axis, one strip. Everything the pair reads — overflow, offset,
   * page size — is chosen by `axis` rather than assumed to be the inline one, so the same
   * code drives a column of thumbs and a row of them. The 150ms resize debounce now also
   * covers the AXIS flip at 1024/1025 for free: every handler re-measures, none caches.
   */
  function bindThumbArrowPair(strip, prev, next, axis) {
    var vertical = axis === 'y';

    function overflow() {
      return vertical ? strip.scrollHeight - strip.clientHeight
                      : strip.scrollWidth - strip.clientWidth;
    }
    function offset() {
      return vertical ? strip.scrollTop : strip.scrollLeft;
    }

    function syncEnds() {
      var max = overflow();
      var pos = offset();
      prev.disabled = pos <= 1;
      next.disabled = pos >= max - 1;
    }

    function sync() {
      var show = overflow() > 1;
      prev.hidden = !show;
      next.hidden = !show;
      if (show) syncEnds();
    }

    function step(dir) {
      if (vertical) strip.scrollBy({ top: dir * strip.clientHeight, behavior: PM_SCROLL });
      else strip.scrollBy({ left: dir * strip.clientWidth, behavior: PM_SCROLL });
    }

    function onPrev() { step(-1); }
    function onNext() { step(1); }
    prev.addEventListener('click', onPrev);
    next.addEventListener('click', onNext);

    var ticking = false;
    function onScroll() {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(function () { ticking = false; syncEnds(); });
    }
    strip.addEventListener('scroll', onScroll, { passive: true });

    var resizeTimer;
    function onResize() {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(sync, 150);
    }
    window.addEventListener('resize', onResize);

    return {
      sync: sync,
      teardown: function () {
        prev.removeEventListener('click', onPrev);
        next.removeEventListener('click', onNext);
        strip.removeEventListener('scroll', onScroll);
        window.removeEventListener('resize', onResize);
        clearTimeout(resizeTimer);
      }
    };
  }

  /**
   * S41-M1-B10 / U3 — keep the active thumb inside the strip's own viewport.
   *
   * scrollIntoView is NOT usable, structurally: it walks EVERY scrollable ancestor up to the
   * viewport, and `block: 'nearest'` picks the ALIGNMENT inside each container, not WHICH
   * containers move. A thumbnails_bottom strip sits under a ~600px image, i.e. below the
   * fold on a laptop, so a variant change would scroll the document out from under the
   * shopper. No option limits it to the first scroll container. scrollTo cannot chain by
   * construction — it names the container.
   *
   * So "nearest" is reimplemented on rects: no movement while the thumb is fully inside,
   * minimum movement otherwise. getBoundingClientRect deltas rather than offsetTop, because
   * offsetParent differs per layout (the abspos shell under thumbnails_left, the gallery
   * root under thumbnails_bottom). Same arithmetic as component-carousel.js:149-156.
   * One scrollTo carrying both axes: a second smooth scrollTo in the same frame cancels the
   * first. Omitted ScrollToOptions members keep the current offset, so writing only `top`
   * never resets `left`.
   */
  function scrollThumbIntoView(root, thumb) {
    if (!thumb) return;
    var strip = root.querySelector('[data-pm-thumbs]');
    if (!strip) return;

    var sb = strip.getBoundingClientRect();
    var tb = thumb.getBoundingClientRect();
    var opts = null;

    if (strip.scrollHeight > strip.clientHeight + 1) {
      var dy = 0;
      if (tb.top < sb.top) dy = tb.top - sb.top;
      else if (tb.bottom > sb.bottom) dy = tb.bottom - sb.bottom;
      if (dy) opts = { top: strip.scrollTop + dy };
    }
    if (strip.scrollWidth > strip.clientWidth + 1) {
      var dx = 0;
      if (tb.left < sb.left) dx = tb.left - sb.left;
      else if (tb.right > sb.right) dx = tb.right - sb.right;
      if (dx) {
        opts = opts || {};
        opts.left = strip.scrollLeft + dx;
      }
    }

    if (!opts) return;
    opts.behavior = PM_SCROLL;
    strip.scrollTo(opts);
  }

  function setupZoomLoader(root, behavior) {
    var loaded = false;
    var mainEl = root.querySelector('[data-pm-main]');
    if (!mainEl) return;

    function loadModule(initialIndex) {
      if (loaded) return;
      loaded = true;

      var moduleName = behavior === 'click_lightbox' ? 'pm-lightbox' : 'pm-hover-zoom';
      var jsUrl = root.getAttribute('data-asset-url-' + moduleName);
      var cssUrl = root.getAttribute('data-asset-url-' + moduleName + '-css');
      if (!jsUrl || !cssUrl) return;

      var link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = cssUrl;
      document.head.appendChild(link);

      var script = document.createElement('script');
      script.src = jsUrl;
      script.onload = function () {
        var mod = behavior === 'click_lightbox' ? window.__pmLightbox : window.__pmHoverZoom;
        if (!mod || typeof mod.init !== 'function') return;
        mod.init(root);
        if (behavior === 'click_lightbox' && typeof initialIndex === 'number' && typeof mod.open === 'function') {
          mod.open(root, initialIndex);
        }
      };
      document.head.appendChild(script);
    }

    if (behavior === 'click_lightbox') {
      mainEl.addEventListener('click', function (e) {
        if (e.target.closest('[data-pm-nav]')) return;
        if (e.target.closest('[data-pm-dot]')) return;
        if (e.target.closest('video')) return;
        var slide = e.target.closest('.pm-gallery__slide');
        var idx = typeof root._pmIndex === 'number' ? root._pmIndex : 0;
        if (slide) {
          var list = mainEl.querySelectorAll('.pm-gallery__slide');
          for (var i = 0; i < list.length; i++) {
            if (list[i] === slide) { idx = i; break; }
          }
        }
        loadModule(idx);
      }, { once: true });
    } else if (behavior === 'hover_zoom_in_place') {
      var trigger = function () {
        loadModule();
        mainEl.removeEventListener('mouseenter', trigger);
        mainEl.removeEventListener('touchstart', trigger);
      };
      mainEl.addEventListener('mouseenter', trigger, { once: true });
      mainEl.addEventListener('touchstart', trigger, { once: true, passive: true });
    }
  }

  function teardown(root) {
    if (!root || !root[GUARD]) return;
    if (root._pmObserver) {
      root._pmObserver.disconnect();
      root._pmObserver = null;
    }
    if (root._pmVariantHandler) {
      document.removeEventListener('product:variant-changed', root._pmVariantHandler);
      root._pmVariantHandler = null;
    }
    if (root._pmKeyHandler) {
      root.removeEventListener('keydown', root._pmKeyHandler);
      root._pmKeyHandler = null;
    }
    if (root._pmThumbArrowsTeardown) root._pmThumbArrowsTeardown();
    if (root._pmRailScrollTeardown) root._pmRailScrollTeardown();
    root.removeAttribute('data-pm-js');
    delete root[GUARD];
  }

  function initAll(scope) {
    (scope || document).querySelectorAll('[data-pm-gallery]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(); });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', function (e) {
    initAll(e.target);
  });
  document.addEventListener('shopify:section:unload', function (e) {
    e.target.querySelectorAll('[data-pm-gallery]').forEach(teardown);
  });
})();
